import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable, throwError } from 'rxjs';
import { environmentConfig } from '../../environment';
import { switchMap, catchError, map } from 'rxjs/operators';
import * as dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class GoogleService {
  private googleCalendarAPIUrl = "https://www.googleapis.com/calendar/v3/calendars/primary/events";
  private gmailAPIUrl = 'https://www.googleapis.com/gmail/v1/users/me/messages';
  private googleDriveFilesAPIUrl = 'https://www.googleapis.com/drive/v3/files';
  private supportsAllDrives = true;
  private includeItemsFromAllDrives = true;

  private apiUrl = environmentConfig.apiBaseUrl;
  private fetchAuthenticated(
    url: string,
    method: 'GET' | 'POST' | 'PUT',
    token: string,
    bodyData?: any
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    const httpOptions = { headers };
    let httpCall: Observable<any>;
    if (method === 'GET') {
      httpCall = this.http.get(url, httpOptions);
    } else if (method === 'POST') {
      httpCall = this.http.post(url, bodyData, httpOptions);
    } else if (method === 'PUT') {
      httpCall = this.http.put(url, bodyData, httpOptions);
    } else {
      return throwError(() => new Error('Unsupported method'));
    }
    return httpCall.pipe(
      catchError(error => {
        console.error('❌ HTTP Error:', error);
        return throwError(() => error);
      })
    );
  }

  constructor(private http: HttpClient, private authService: AuthService) { }

  //CALENDAR
  getCalendarEvents(): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) {
          console.error("No Google OAuth Token Found. User may need to log in again.");
          return throwError(() => new Error("No Google OAuth Token Found."));
        }
        const timeMin = dayjs().subtract(90, 'days').toISOString();

        return this.http.get<any>(
          `${this.googleCalendarAPIUrl}?timeMin=${encodeURIComponent(timeMin)}&orderBy=startTime&singleEvents=true`,
          { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) }
        );
      }),
      catchError(error => {
        console.error("Google Calendar API Error:", error);
        return throwError(() => new Error("Google Calendar API Error"));
      })
    );
  }


  getCalendarEventsWeekly(dtFrom: any, dtThru: any, token: string): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) {
          console.error("No Google OAuth Token Found. User may need to log in again.");
          return throwError(() => new Error("No Google OAuth Token Found."));
        }
        const dt = new Date();
        const wkday = dt.getDay() + 1; // current week day (zero based, so offset one)
        const togo = 7 - dt.getDay();

        const timeMin = (dtFrom == "") ?
          new Date(Date.now() - wkday * 24 * 60 * 60 * 1000).toISOString() :
          new Date(dtFrom).toISOString();
        const timeMax = (dtThru == "") ?
          new Date(Date.now() + togo * 24 * 60 * 60 * 1000).toISOString() :
          new Date(dtThru).toISOString()

        return this.http.get<any>(`${this.googleCalendarAPIUrl}?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}&orderBy=startTime&singleEvents=true`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      }),
      catchError(error => {
        console.error("Google Calendar API Error:", error);
        return throwError(() => new Error("Google Calendar API Error"));
      })
    );
  }

  getUpcomingCalendarEvents(): Observable<any> {
    console.log("Requesting Upcoming Calendar Events...");
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) {
          console.error("No Google OAuth Token Found. User may need to log in again.");
          return throwError(() => new Error("No Google OAuth Token Found."));
        }
        const timeMin = new Date().toISOString();
        return this.http.get<any>(`${this.googleCalendarAPIUrl}?timeMin=${encodeURIComponent(timeMin)}&orderBy=startTime&singleEvents=true`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      }),
      catchError(error => {
        console.error("Google Calendar API Error:", error);
        return throwError(() => new Error("Google Calendar API Error"));
      })
    );
  }

  //GMAIL
  getUnreadEmailCount(): Observable<number> {
    console.log("Requesting Unread Email Count...");
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) {
          console.error("No Google OAuth Token Found.");
          return throwError(() => new Error("No Google OAuth Token Found."));
        }
        const query = "in:inbox is:unread NOT category:promotions NOT category:social";
        return this.http.get<any>(`${this.gmailAPIUrl}?q=${encodeURIComponent(query)}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        }).pipe(
          map(response => response?.messages?.length || 0),
          catchError(error => {
            console.error("Gmail API Error:", error);
            return throwError(() => new Error("Gmail API Error"));
          })
        );
      })
    );
  }

  //DRIVE
  formatClientRootName(client: { code: string; name: string }): string {
    return `${client.code}-${client.name}`;
  }

  findSharedClientRootByName(folderName: string): Observable<any | null> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const q = [
          'sharedWithMe',
          `name='${folderName.replace(/'/g, "\\'")}'`,
          "mimeType='application/vnd.google-apps.folder'",
          'trashed=false'
        ].join(' and ');
        const params =
          `q=${encodeURIComponent(q)}` +
          `&pageSize=10` +
          `&fields=files(id,name,mimeType,driveId,parents,webViewLink,iconLink),nextPageToken` +
          `&supportsAllDrives=${this.supportsAllDrives}` +
          `&includeItemsFromAllDrives=${this.includeItemsFromAllDrives}`;

        return this.http.get<any>(`${this.googleDriveFilesAPIUrl}?${params}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      }),
      map(r => (r?.files?.[0] ?? null))
    );
  }

  getClientRoot(client: { code: string; name: string }): Observable<any | null> {
    const folderName = this.formatClientRootName(client);
    return this.findSharedClientRootByName(folderName);
  }

  listFolderChildren(folderId: string, pageSize = 100, pageToken = ''): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const q = `'${folderId}' in parents and trashed=false`;
        const params =
          `q=${encodeURIComponent(q)}` +
          `&pageSize=${pageSize}` +
          (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '') +
          `&fields=files(id,name,mimeType,modifiedTime,webViewLink,iconLink),nextPageToken` +
          `&supportsAllDrives=${this.supportsAllDrives}` +
          `&includeItemsFromAllDrives=${this.includeItemsFromAllDrives}`;
        return this.http.get<any>(`${this.googleDriveFilesAPIUrl}?${params}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      })
    );
  }

  listSubfolders(folderId: string, pageSize = 100): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const q = [
          `'${folderId}' in parents`,
          "mimeType='application/vnd.google-apps.folder'",
          'trashed=false'
        ].join(' and ');
        const params =
          `q=${encodeURIComponent(q)}` +
          `&pageSize=${pageSize}` +
          `&fields=files(id,name,parents),nextPageToken` +
          `&supportsAllDrives=${this.supportsAllDrives}` +
          `&includeItemsFromAllDrives=${this.includeItemsFromAllDrives}`;
        return this.http.get<any>(`${this.googleDriveFilesAPIUrl}?${params}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      })
    );
  }

  //USER DRIVE
  getUserDriveRoot(): Observable<any> {
    // My Drive root = 'root'
    return new Observable(observer => {
      observer.next({ id: 'root', name: 'My Drive' });
      observer.complete();
    });
  }

  getUserDriveFiles(folderId = 'root', pageSize = 100, pageToken = ''): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const q = `'${folderId}' in parents and trashed=false`;
        const params =
          `q=${encodeURIComponent(q)}` +
          `&pageSize=${pageSize}` +
          (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '') +
          `&fields=files(id,name,mimeType,modifiedTime,webViewLink,iconLink),nextPageToken` +
          `&supportsAllDrives=${this.supportsAllDrives}` +
          `&includeItemsFromAllDrives=${this.includeItemsFromAllDrives}`;
        return this.http.get<any>(`${this.googleDriveFilesAPIUrl}?${params}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      })
    );
  }

  getUserSharedDrives(): Observable<any[]> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const fields = 'drives(id,name)';
        return this.http.get<any>(`https://www.googleapis.com/drive/v3/drives?fields=${fields}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      }),
      map(res => res?.drives ?? []),
      catchError(err => {
        console.error('Error fetching shared drives:', err);
        return throwError(() => new Error('Error fetching shared drives.'));
      })
    );
  }

  getUserSharedDriveFiles(driveId: string, folderId?: string, pageSize = 100, pageToken = ''): Observable<any> {
    return this.authService.getToken().pipe(
      switchMap(token => {
        if (!token) return throwError(() => new Error('No Google OAuth Token Found.'));
        const q = folderId ? `'${folderId}' in parents and trashed=false` : `'${driveId}' in parents and trashed=false`;
        const params =
          `q=${encodeURIComponent(q)}` +
          `&corpora=drive` +
          `&driveId=${driveId}` +
          `&pageSize=${pageSize}` +
          (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '') +
          `&fields=files(id,name,mimeType,modifiedTime,webViewLink,iconLink),nextPageToken` +
          `&supportsAllDrives=${this.supportsAllDrives}` +
          `&includeItemsFromAllDrives=${this.includeItemsFromAllDrives}`;
        return this.http.get<any>(`${this.googleDriveFilesAPIUrl}?${params}`, {
          headers: new HttpHeaders({ Authorization: `Bearer ${token}` })
        });
      })
    );
  }
  createDocInFolder(payload: any) {
    const url = `${this.apiUrl}/api/google/createdoc-in-folder`;
    console.log("Create Doc in Folder",url);
    return this.http.post(url, payload);
  }
}
